"use client";

import { type ReactNode, createContext, useContext, useMemo } from "react";

import type { ModalComponent, ModalComponentWithArgs } from "./modal-builder";
import type { ModalWithOptionalDeduplicationKey } from "./modal-id";
import { useModalCloseOperations } from "./operations/use-modal-close-operations";
import { useModalOpenOperations } from "./operations/use-modal-open-operations";
import { useModalReplaceOperations } from "./operations/use-modal-replace-operations";

type OpenModalProps<T extends ModalComponent<never>> =
  T extends ModalComponentWithArgs<never, infer Args>
    ? [ModalWithOptionalDeduplicationKey<T>, Args]
    : [ModalWithOptionalDeduplicationKey<T>];

type ReplaceModalProps<T extends ModalComponent<never>> =
  T extends ModalComponentWithArgs<never, infer Args>
    ? [ModalWithOptionalDeduplicationKey<ModalComponent<never>>, ModalWithOptionalDeduplicationKey<T>, Args]
    : [ModalWithOptionalDeduplicationKey<ModalComponent<never>>, ModalWithOptionalDeduplicationKey<T>];

interface ModalsStackContextValue {
  openModal: <T extends ModalComponent<never>>(...props: OpenModalProps<T>) => void;
  closeModal: <T extends ModalComponent<never>>(modal: ModalWithOptionalDeduplicationKey<T>) => void;
  replaceModal: <T extends ModalComponent<never>>(...props: ReplaceModalProps<T>) => void;
  replaceBackModal: <T extends ModalComponent<never>>(...props: ReplaceModalProps<T>) => void;
  dismissAllModals: () => void;
}

const ModalStackContext = createContext<ModalsStackContextValue | null>(null);

interface ModalsStackContextProviderProps {
  children?: ReactNode;
}

export const ModalsStackContextProvider = ({ children }: ModalsStackContextProviderProps) => {
  const { closeUserTriggeredModal, closeAllUserTriggeredModals } = useModalCloseOperations();
  const { openUserTriggeredModal } = useModalOpenOperations();
  const { replaceUserTriggeredModal, replaceBackUserTriggeredModal } = useModalReplaceOperations();

  const value = useMemo(
    () => ({
      openModal: openUserTriggeredModal,
      closeModal: closeUserTriggeredModal,
      replaceModal: replaceUserTriggeredModal,
      replaceBackModal: replaceBackUserTriggeredModal,
      dismissAllModals: closeAllUserTriggeredModals,
    }),
    [
      openUserTriggeredModal,
      closeUserTriggeredModal,
      replaceUserTriggeredModal,
      replaceBackUserTriggeredModal,
      closeAllUserTriggeredModals,
    ],
  );

  return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
};

export const useModalsStackContext = () => {
  const context = useContext(ModalStackContext);
  if (!context) {
    throw new Error("useStackableModalsContext must be used within a ModalStackProvider");
  }

  return context;
};
